(function () {
  'use strict';

  const instances = new Map();
  const MOBILE_MQ = '(max-width: 768px)';
  const SWIPE_THRESHOLD = 50;

  class RgEssReliabilitySlider {
    constructor(root) {
      this.root = root;
      this.viewport = root.querySelector('[data-rg-ess-reliability-viewport]');
      this.track = root.querySelector('[data-rg-ess-reliability-track]');
      this.slides = Array.from(root.querySelectorAll('[data-rg-ess-reliability-slide]'));
      this.descriptionPanels = Array.from(root.querySelectorAll('[data-rg-ess-reliability-description]'));
      this.tabs = Array.from(root.querySelectorAll('[data-rg-ess-reliability-tab]'));
      this.prevButton = root.querySelector('[data-rg-ess-reliability-prev]');
      this.nextButton = root.querySelector('[data-rg-ess-reliability-next]');
      this.mobileQuery = window.matchMedia(MOBILE_MQ);
      this.reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.currentIndex = 0;
      this.autoplayTimer = null;
      this.descriptionCleanupTimer = null;
      this.isVisible = true;
      this.touchStartX = 0;
      this.touchStartY = 0;
      this.touchDeltaX = 0;
      this.isSwiping = false;

      if (!this.viewport || !this.track || !this.slides.length) return;

      this.onPrev = this.onPrev.bind(this);
      this.onNext = this.onNext.bind(this);
      this.onVisibilityChange = this.onVisibilityChange.bind(this);
      this.onModeChange = this.onModeChange.bind(this);
      this.onTouchStart = this.onTouchStart.bind(this);
      this.onTouchMove = this.onTouchMove.bind(this);
      this.onTouchEnd = this.onTouchEnd.bind(this);

      this.mediaPanels = Array.from(root.querySelectorAll('[data-rg-ess-reliability-media]'));
      this.bindEvents();
      this.update(0, { restartAutoplay: false });
      this.startAutoplay();
    }

    get count() {
      return this.slides.length;
    }

    isMobile() {
      return this.mobileQuery.matches;
    }

    autoplayEnabled() {
      const value = this.isMobile()
        ? this.root.dataset.mobileAutoplay
        : this.root.dataset.desktopAutoplay;
      return value === 'true';
    }

    autoplayInterval() {
      const value = Number.parseInt(this.root.dataset.autoplayInterval, 10);
      return Number.isNaN(value) ? 5000 : Math.max(2000, value * 1000);
    }

    transitionDuration() {
      const value = Number.parseFloat(this.root.dataset.transitionDuration);
      return Number.isNaN(value) ? 200 : Math.max(100, value * 1000);
    }

    shouldAutoplay() {
      return (
        this.count > 1 &&
        this.autoplayEnabled() &&
        !this.reducedMotionQuery.matches &&
        !document.hidden &&
        this.isVisible
      );
    }

    update(index, options = {}) {
      const previousIndex = this.currentIndex;
      if (options.loop && this.count > 0) {
        this.currentIndex = (index + this.count) % this.count;
      } else {
        this.currentIndex = Math.max(0, Math.min(this.count - 1, index));
      }

      this.slides.forEach((slide, slideIndex) => {
        const active = slideIndex === this.currentIndex;
        slide.classList.toggle('is-active', active);
        slide.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      this.descriptionPanels.forEach((panel, panelIndex) => {
        const active = panelIndex === this.currentIndex;
        const leaving = panelIndex === previousIndex && previousIndex !== this.currentIndex;
        panel.classList.toggle('is-leaving', leaving);
        panel.classList.toggle('is-active', active);
        panel.setAttribute('aria-hidden', active ? 'false' : 'true');
      });

      if (this.descriptionCleanupTimer) window.clearTimeout(this.descriptionCleanupTimer);
      this.descriptionCleanupTimer = window.setTimeout(() => {
        this.descriptionPanels.forEach((panel) => panel.classList.remove('is-leaving'));
        this.descriptionCleanupTimer = null;
      }, this.transitionDuration() + 50);

      this.tabs.forEach((tab, tabIndex) => {
        const active = tabIndex === this.currentIndex;
        tab.classList.toggle('is-active', active);
        tab.setAttribute('aria-selected', active ? 'true' : 'false');
        tab.setAttribute('tabindex', active ? '0' : '-1');
      });

      if (this.prevButton) this.prevButton.disabled = this.count <= 1;
      if (this.nextButton) this.nextButton.disabled = this.count <= 1;

      this.syncVideos();
      this.keepActiveTabVisible();

      if (options.restartAutoplay !== false) {
        this.startAutoplay();
      }
    }

    visibleVideo(panel) {
      if (!panel) return null;
      const selector = this.isMobile()
        ? '.rg-ess-reliability__media-device--mobile video'
        : '.rg-ess-reliability__media-device--desktop video';
      return panel.querySelector(selector);
    }

    syncVideos() {
      this.mediaPanels.forEach((panel, index) => {
        panel.querySelectorAll('video').forEach((video) => video.pause());
        if (index !== this.currentIndex) return;
        const video = this.visibleVideo(panel);
        if (!video) return;
        video.muted = true;
        video.loop = true;
        video.play().catch(() => {});
      });
    }

    keepActiveTabVisible() {
      if (!this.isMobile()) return;
      const tab = this.tabs[this.currentIndex];
      const viewport = tab?.closest('.rg-ess-reliability__tabs');
      if (!tab || !viewport) return;

      const tabLeft = tab.offsetLeft;
      const tabRight = tabLeft + tab.offsetWidth;
      const viewportLeft = viewport.scrollLeft;
      const viewportRight = viewportLeft + viewport.clientWidth;

      if (tabLeft < viewportLeft) {
        viewport.scrollTo({ left: tabLeft, behavior: 'smooth' });
      } else if (tabRight > viewportRight) {
        viewport.scrollTo({ left: tabRight - viewport.clientWidth, behavior: 'smooth' });
      }
    }

    stopAutoplay() {
      if (this.autoplayTimer) window.clearTimeout(this.autoplayTimer);
      this.autoplayTimer = null;
      this.root.classList.remove('is-autoplay-running');
    }

    startAutoplay() {
      this.stopAutoplay();
      if (!this.shouldAutoplay()) return;

      void this.nextButton?.offsetWidth;
      this.root.classList.add('is-autoplay-running');
      this.autoplayTimer = window.setTimeout(() => {
        this.autoplayTimer = null;
        this.update(this.currentIndex + 1, { loop: true });
      }, this.autoplayInterval());
    }

    onPrev() {
      this.update(this.currentIndex - 1, { loop: true });
    }

    onNext() {
      this.update(this.currentIndex + 1, { loop: true });
    }

    onVisibilityChange() {
      if (document.hidden) {
        this.stopAutoplay();
      } else {
        this.startAutoplay();
        this.syncVideos();
      }
    }

    onModeChange() {
      this.stopAutoplay();
      this.syncVideos();
      this.keepActiveTabVisible();
      this.startAutoplay();
    }

    onTouchStart(event) {
      if (!this.isMobile() || event.touches.length !== 1) return;
      this.touchStartX = event.touches[0].clientX;
      this.touchStartY = event.touches[0].clientY;
      this.touchDeltaX = 0;
      this.isSwiping = false;
      this.stopAutoplay();
    }

    onTouchMove(event) {
      if (!this.isMobile() || event.touches.length !== 1) return;
      const deltaX = event.touches[0].clientX - this.touchStartX;
      const deltaY = event.touches[0].clientY - this.touchStartY;

      if (!this.isSwiping && Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 10) {
        this.isSwiping = true;
      }

      if (this.isSwiping) {
        this.touchDeltaX = deltaX;
        event.preventDefault();
      }
    }

    onTouchEnd() {
      if (!this.isMobile()) return;

      if (this.isSwiping) {
        if (this.touchDeltaX <= -SWIPE_THRESHOLD) {
          this.update(this.currentIndex + 1, { loop: true });
        } else if (this.touchDeltaX >= SWIPE_THRESHOLD) {
          this.update(this.currentIndex - 1, { loop: true });
        }
      }

      this.isSwiping = false;
      this.startAutoplay();
    }

    bindEvents() {
      if (this.prevButton) this.prevButton.addEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.addEventListener('click', this.onNext);

      this.tabs.forEach((tab, index) => {
        tab.addEventListener('click', () => this.update(index));
      });

      document.addEventListener('visibilitychange', this.onVisibilityChange);

      if (this.mobileQuery.addEventListener) {
        this.mobileQuery.addEventListener('change', this.onModeChange);
      } else {
        this.mobileQuery.addListener(this.onModeChange);
      }

      this.viewport.addEventListener('touchstart', this.onTouchStart, { passive: true });
      this.viewport.addEventListener('touchmove', this.onTouchMove, { passive: false });
      this.viewport.addEventListener('touchend', this.onTouchEnd, { passive: true });
      this.viewport.addEventListener('touchcancel', this.onTouchEnd, { passive: true });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            this.isVisible = Boolean(entries[0]?.isIntersecting);
            if (this.isVisible) {
              this.startAutoplay();
              this.syncVideos();
            } else {
              this.stopAutoplay();
              this.mediaPanels.forEach((panel) => {
                panel.querySelectorAll('video').forEach((video) => video.pause());
              });
            }
          },
          { threshold: 0.2 }
        );
        this.observer.observe(this.root);
      }
    }

    destroy() {
      this.stopAutoplay();
      if (this.descriptionCleanupTimer) window.clearTimeout(this.descriptionCleanupTimer);
      if (this.prevButton) this.prevButton.removeEventListener('click', this.onPrev);
      if (this.nextButton) this.nextButton.removeEventListener('click', this.onNext);
      document.removeEventListener('visibilitychange', this.onVisibilityChange);

      if (this.mobileQuery.removeEventListener) {
        this.mobileQuery.removeEventListener('change', this.onModeChange);
      } else {
        this.mobileQuery.removeListener(this.onModeChange);
      }

      this.viewport.removeEventListener('touchstart', this.onTouchStart);
      this.viewport.removeEventListener('touchmove', this.onTouchMove);
      this.viewport.removeEventListener('touchend', this.onTouchEnd);
      this.viewport.removeEventListener('touchcancel', this.onTouchEnd);

      if (this.observer) this.observer.disconnect();
      this.root.classList.remove('is-autoplay-running');
    }
  }

  function initReliabilitySliders(scope = document) {
    scope.querySelectorAll('[data-rg-ess-reliability]').forEach((root) => {
      if (!root.id) return;
      if (instances.has(root.id)) instances.get(root.id).destroy();
      instances.set(root.id, new RgEssReliabilitySlider(root));
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initReliabilitySliders());
  } else {
    initReliabilitySliders();
  }

  document.addEventListener('shopify:section:load', (event) => {
    initReliabilitySliders(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const root = event.target.querySelector('[data-rg-ess-reliability]');
    if (!root?.id || !instances.has(root.id)) return;
    instances.get(root.id).destroy();
    instances.delete(root.id);
  });
})();
